import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

/**
 * Role flags PCT sets as response headers ("true"/"false") and the BFF
 * forwards (PCTCommonConstants.RESPONSE_HEADER_IS_*_USER).
 */
export type UserRoleFlags = {
  isAdminUser: boolean;
  isLimitedUser: boolean;
  isGOSUser: boolean;
};

const HEADER_IS_ADMIN_USER = "isadminuser";
const HEADER_IS_LIMITED_USER = "islimiteduser";
const HEADER_IS_GOS_USER = "isgosuser";

// The flags are taken only from the GrECo companies call the app makes at
// startup: other calls (taxonomy) can be served from the APIM cache and would
// then carry the headers of whichever user filled the cache.
const roleSourceUrl = `${process.env.REACT_APP_BASE_URL}/grecocompany`;

// null until the GrECo companies response carrying the headers arrives
let userRoleFlags: UserRoleFlags | null = null;
const listeners = new Set<() => void>();

/** Reads the role headers from the GrECo companies response (success or error) and publishes changes. */
export const captureUserRoleHeaders = (response?: AxiosResponse) => {
  const headers = response?.headers;
  if (!headers || response.config?.url?.split("?")[0] !== roleSourceUrl) return;

  const values = [
    HEADER_IS_ADMIN_USER,
    HEADER_IS_LIMITED_USER,
    HEADER_IS_GOS_USER,
  ].map((name) => headers[name]);
  if (values.every((value) => value === undefined || value === null)) return;

  const [isAdminUser, isLimitedUser, isGOSUser] = values.map(
    (value) => String(value).toLowerCase() === "true"
  );
  if (
    userRoleFlags?.isAdminUser === isAdminUser &&
    userRoleFlags?.isLimitedUser === isLimitedUser &&
    userRoleFlags?.isGOSUser === isGOSUser
  ) {
    return;
  }

  userRoleFlags = { isAdminUser, isLimitedUser, isGOSUser };
  listeners.forEach((listener) => listener());
};

/** Current role flags; null while no BFF response with role headers has been received. */
export const useUserRoleFlags = () => {
  const [flags, setFlags] = useState(userRoleFlags);
  useEffect(() => {
    const listener = () => setFlags(userRoleFlags);
    listeners.add(listener);
    // flags may have arrived between the first render and this subscription
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return flags;
};

/**
 * Limited user who is neither admin nor GOS user: may only use the Payment plans
 * page and, read-only (no creating or deleting reminders), the Reminders page.
 */
export const isLimitedOnlyUser = (flags: UserRoleFlags | null) =>
  !!flags && flags.isLimitedUser && !flags.isAdminUser && !flags.isGOSUser;
